import type {
  AiApplicationComponentsResponseDto,
  AiComponentDto,
} from '@terra/shared-contracts-ai-applications';
import { getAiComponentMetadataString } from './aiComponentMetadata';

export const COMPOSITION_CARD_WIDTH = 300;
export const COMPOSITION_CARD_HEIGHT = 150;

const COLUMN_GAP = 260;
const ROW_GAP = 30;

export const COMPOSITION_VISUAL_KINDS = [
  'AGENT',
  'ROUTER',
  'MIDDLEWARE',
  'MEMORY',
  'TOOL_FUNCTION',
  'TOOL_HOSTED',
  'TOOL',
] as const;

export type CompositionVisualKind = (typeof COMPOSITION_VISUAL_KINDS)[number];

export type CompositionCardVisibility =
  | 'shown'
  | 'background'
  | 'unknown'
  | 'verified'
  | 'none';

export function resolveCompositionVisualKind(
  component: AiComponentDto,
): CompositionVisualKind {
  const nodeType = getAiComponentMetadataString({
    metadata: component.metadata,
    field: 'nodeType',
  });
  const fromNodeType = nodeType ? kindFromNodeType(nodeType) : null;

  if (fromNodeType) {
    return fromNodeType;
  }

  switch (component.type) {
    case 'AGENT':
    case 'SUB_AGENT':
    case 'MODEL':
      return 'AGENT';
    case 'MEMORY':
    case 'RAG':
      return 'MEMORY';
    case 'GUARDRAIL':
      return 'MIDDLEWARE';
    default:
      return 'TOOL';
  }
}

export function resolveCompositionRoleText(component: AiComponentDto): string {
  return (
    getAiComponentMetadataString({
      metadata: component.metadata,
      field: 'nodeType',
    }) ??
    component.role ??
    component.type
  );
}

export function resolveCompositionCardDescription(
  component: AiComponentDto,
): string | null {
  return (
    getAiComponentMetadataString({
      metadata: component.metadata,
      field: 'discriminator',
    }) ??
    (component.description?.trim() || null)
  );
}

export function resolveCompositionCardVisibility(
  component: AiComponentDto,
): CompositionCardVisibility {
  const invocationVisibility = getAiComponentMetadataString({
    metadata: component.metadata,
    field: 'invocationVisibility',
  });

  if (invocationVisibility === 'user_visible') {
    return 'shown';
  }

  if (invocationVisibility === 'hidden') {
    return 'background';
  }

  if (invocationVisibility === 'unknown') {
    return 'unknown';
  }

  if (component.verificationStatus === 'RUNTIME_VERIFIED') {
    return 'verified';
  }

  return 'none';
}

export function compositionRelationLabel({
  type,
  label,
}: {
  type: string;
  label: string | null;
}): string {
  const storedLabel = label?.trim();

  if (storedLabel) {
    return storedLabel;
  }

  return type.toLowerCase().replaceAll('_', ' ');
}

export function compositionEdgePath({
  x1,
  y1,
  x2,
  y2,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}): { d: string; labelX: number; labelY: number } {
  if (x2 <= x1 + 8) {
    return {
      d: `M${x1},${y1} C${x1 + 40},${y1} ${x2 - 40},${y2} ${x2},${y2}`,
      labelX: (x1 + x2) / 2,
      labelY: (y1 + y2) / 2,
    };
  }

  const elbowX = x1 + 40;

  if (Math.abs(y2 - y1) < 1) {
    return {
      d: `M${x1},${y1} H${x2}`,
      labelX: (elbowX + x2) / 2,
      labelY: y2,
    };
  }

  const radius = Math.min(10, Math.abs(y2 - y1) / 2);
  const direction = y2 > y1 ? 1 : -1;

  return {
    d: `M${x1},${y1} H${elbowX - radius} Q${elbowX},${y1} ${elbowX},${y1 + direction * radius} V${y2 - direction * radius} Q${elbowX},${y2} ${elbowX + radius},${y2} H${x2}`,
    labelX: (elbowX + x2) / 2,
    labelY: y2,
  };
}

export function compositionLayoutEndpoints({
  relation,
  sourceType,
}: {
  relation: { sourceId: string; targetId: string; type: string };
  sourceType: string | undefined;
}): { parentId: string; childId: string } {
  const guardrailPointsAtParent =
    relation.type === 'REQUIRES_APPROVAL' && sourceType === 'GUARDRAIL';

  return guardrailPointsAtParent
    ? { parentId: relation.targetId, childId: relation.sourceId }
    : { parentId: relation.sourceId, childId: relation.targetId };
}

export function layoutCompositionGraph({
  components,
  relations,
}: {
  components: AiComponentDto[];
  relations: AiApplicationComponentsResponseDto['relations'];
}): {
  positions: Map<string, { x: number; y: number }>;
  rootId: string | null;
} {
  const ids = components.map((component) => component.id);
  const idSet = new Set(ids);
  const byId = new Map(
    components.map((component) => [component.id, component]),
  );
  const outgoing = new Map<string, string[]>(ids.map((id) => [id, []]));
  const incoming = new Set<string>();

  for (const relation of relations) {
    if (
      !idSet.has(relation.sourceId) ||
      !idSet.has(relation.targetId) ||
      relation.sourceId === relation.targetId
    ) {
      continue;
    }

    const { parentId, childId } = compositionLayoutEndpoints({
      relation,
      sourceType: byId.get(relation.sourceId)?.type,
    });

    outgoing.get(parentId)?.push(childId);
    incoming.add(childId);
  }

  const rank = new Map<string, number>(ids.map((id) => [id, 0]));

  for (let pass = 0; pass < ids.length; pass += 1) {
    for (const sourceId of ids) {
      const sourceRank = rank.get(sourceId) ?? 0;

      for (const targetId of outgoing.get(sourceId) ?? []) {
        const nextRank = sourceRank + 1;

        if ((rank.get(targetId) ?? 0) < nextRank) {
          rank.set(targetId, nextRank);
        }
      }
    }
  }

  const byColumn = new Map<number, AiComponentDto[]>();

  for (const component of components) {
    const column = rank.get(component.id) ?? 0;
    const columnItems = byColumn.get(column) ?? [];

    columnItems.push(component);
    byColumn.set(column, columnItems);
  }

  const positions = new Map<string, { x: number; y: number }>();

  for (const [column, columnItems] of byColumn) {
    const ordered = [...columnItems].sort(compareCompositionNodes);

    ordered.forEach((component, row) => {
      positions.set(component.id, {
        x: column * (COMPOSITION_CARD_WIDTH + COLUMN_GAP),
        y: row * (COMPOSITION_CARD_HEIGHT + ROW_GAP),
      });
    });
  }

  const roots = components.filter((component) => !incoming.has(component.id));
  const root =
    roots.find((component) => component.type === 'AGENT') ??
    roots.find((component) => component.type === 'SUB_AGENT') ??
    components.find((component) => component.type === 'AGENT') ??
    components.find((component) => component.type === 'SUB_AGENT') ??
    components[0] ??
    null;

  return { positions, rootId: root?.id ?? null };
}

function kindFromNodeType(nodeType: string): CompositionVisualKind | null {
  switch (nodeType) {
    case 'AGENT':
    case 'SUB_AGENT':
    case 'ENTRY':
    case 'LLM':
      return 'AGENT';
    case 'ROUTER':
      return 'ROUTER';
    case 'MIDDLEWARE':
    case 'HOOK':
    case 'SKILL':
    case 'GUARDRAIL':
      return 'MIDDLEWARE';
    case 'MEMORY':
    case 'STATE':
      return 'MEMORY';
    case 'TOOL_FUNCTION':
    case 'TOOL_MCP':
      return 'TOOL_FUNCTION';
    case 'TOOL_HOSTED':
      return 'TOOL_HOSTED';
    case 'TOOL':
      return 'TOOL';
    default:
      return null;
  }
}

function compareCompositionNodes(
  left: AiComponentDto,
  right: AiComponentDto,
): number {
  const leftAgent = left.type === 'AGENT' || left.type === 'SUB_AGENT' ? 0 : 1;
  const rightAgent =
    right.type === 'AGENT' || right.type === 'SUB_AGENT' ? 0 : 1;

  if (leftAgent !== rightAgent) {
    return leftAgent - rightAgent;
  }

  return left.name.localeCompare(right.name);
}
